import globalState from '../globalState';
import api, { apiUpload } from './api';
import { createSocketCache, waitForUpdate } from '@/api/socketConnection';
import { Bucket } from 'common/types/bucket';
import { Receipt } from 'common/types/receipt';
import { ReceiptDraft } from 'common/types/receiptDraft';
import type { UploadJob } from 'common/types/uploadJob';
import { findOrDefault } from 'cross-state';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { createQueue } from 'schummar-queue';
import { z } from 'zod';

const receiptApi = new (class {
    uploadQueue = createQueue();

    get = createSocketCache<{ receipt: Receipt; bucket: Bucket }, [receiptId: string]>({
        async fetch(receiptId) {
            const response = await api.get(`/api/receipts/${receiptId}`).json();
            const bucket = Bucket.parse(response);
            const receipt = bucket.receipts.find((receipt) => receipt.id === receiptId)!;
            return { receipt, bucket };
        },
        onBucketUpdate(bucket, cache) {
            for (const receipt of bucket.receipts) {
                cache(receipt.id).updateValue({ receipt, bucket });
            }
        },
        onBucketDelete({ receiptIds }, cache) {
            setTimeout(() => {
                for (const receiptId of receiptIds) {
                    cache(receiptId).clear();
                }
            }, 1000);
        },
    });

    async create(
        draft: ReceiptDraft,
        tripId?: string,
    ): Promise<{ receipt: Receipt; bucket: Bucket }> {
        const response = await api.post(`/api/receipts`, { json: { ...draft, tripId } }).json();
        return z.object({ receipt: Receipt, bucket: Bucket }).parse(response);
    }

    async update(receiptId: string, draft: ReceiptDraft) {
        await waitForUpdate(() => {
            return api.patch(`/api/receipts/${receiptId}`, { json: draft }).json();
        });
    }

    private async addFile({ id, receiptId, file }: UploadJob) {
        try {
            await apiUpload(`/api/receipts/${receiptId}/files`, file, (loaded, total) => {
                globalState.set('uploadJobs', (jobs) =>
                    jobs.map((job) => (job.id === id ? { ...job, loaded, total } : job)),
                );
            });

            globalState.set('uploadJobs', (jobs) => jobs.filter((job) => job.id !== id));
        } catch (error) {
            globalState.set('uploadJobs', (jobs) =>
                jobs.map((job) => (job.id === id ? { ...job, error } : job)),
            );
        }
    }

    startUpload(receiptId: string, file: Blob) {
        globalState.update('uploadJobs', (jobs) => {
            const job = findOrDefault(
                jobs,
                (job) => job.receiptId === receiptId && job.file === file,
                { id: nanoid(), receiptId, file },
            );
            job.loaded = 0;
            delete job.error;
        });

        this.uploadQueue.schedule(() =>
            this.addFile(
                globalState
                    .get()
                    .uploadJobs.find((job) => job.receiptId === receiptId && job.file === file)!,
            ),
        );
    }

    cancelUpload(receiptId: string, file: Blob) {
        globalState.set('uploadJobs', (jobs) =>
            jobs.filter((job) => !(job.receiptId === receiptId && job.file === file)),
        );
    }

    async rotate(receiptId: string, fileId: string, rotation: number) {
        await waitForUpdate(() => {
            return api
                .post(`/api/receipts/${receiptId}/rotate/${rotation}/${encodeURIComponent(fileId)}`)
                .json();
        });
    }

    async removeFile(receiptId: string, id: string) {
        await waitForUpdate(() => {
            return api.delete(`/api/receipts/${receiptId}/files/${id}`).json();
        });
    }

    async addComment(receiptId: string, text: string) {
        await waitForUpdate(() => {
            return api.post(`/api/receipts/${receiptId}/comments`, { json: text }).json();
        });
    }

    async addTripBatch(receiptIds: string[], tripId: string) {
        await waitForUpdate(async () => {
            const response = await api
                .put(`/api/receipts/tripBatch`, {
                    json: {
                        receiptIds,
                        tripId,
                    },
                })
                .json();

            const { errors } = z
                .object({ errors: z.record(z.string(), z.string()) })
                .parse(response);
            if (!_.isEmpty(errors)) throw _.values(errors).join('  ');

            return response;
        });
    }

    async updateDuplicates(receiptId: string) {
        await waitForUpdate(() => {
            return api.post(`/api/receipts/${receiptId}/updateDuplicates`).json();
        });
    }

    async acceptDuplicates(receiptId: string, comment: string | undefined) {
        await waitForUpdate(() => {
            return api
                .post(`/api/receipts/${receiptId}/acceptDuplicates`, {
                    json: { comment },
                })
                .json();
        });
    }

    async reviewDuplicates(receiptId: string, comment: string | undefined) {
        await waitForUpdate(() => {
            return api
                .post(`/api/receipts/${receiptId}/reviewDuplicates`, {
                    json: { comment },
                })
                .json();
        });
    }

    async resetDuplicates(receiptId: string) {
        await waitForUpdate(() => {
            return api.post(`/api/receipts/${receiptId}/resetDuplicates`).json();
        });
    }

    async analyze(receiptId: string) {
        await waitForUpdate(() => {
            return api.post(`/api/receipts/${receiptId}/analyze`).json();
        });
    }
})();
export default receiptApi;

Object.assign(window, { receiptApi });
